import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Tag,
  Divider,
  Alert,
  Button,
  Row,
  Col,
  Tabs,
  Progress,
  notification,
  Space,
  Collapse,
  List,
  Spin,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  DownOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import API from "Common/Client/endpoint";
const { Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
// Enum mapping
const RecommendationStatus = {
  NoReview: 1,
  HighPerformance: 2,
  NeedsTraining: 3,
  NoCoursesMappedForSkill: 4
};
const statusMessages = {
  [RecommendationStatus.NoReview]: {
    title: "Assessment Pending",
    message: "This team member hasn't completed their Performance Review assessment yet.",
    type: "info"
  },
  [RecommendationStatus.HighPerformance]: {
    title: "Strong Performance",
    message: "This team member is meeting all required skill expectations.",
    type: "success"
  },
  [RecommendationStatus.NeedsTraining]: {
    title: "Training Recommendations",
    message: "We've identified skill gaps for this team member. Below are recommended courses to help them improve.",
    type: "warning"
  },
  [RecommendationStatus.NoCoursesMappedForSkill]: {
    title: "No Training Available",
    message: "We've identified skill gaps but don't have specific courses mapped yet for this team member.",
    type: "error"
  }
};
const TrainingRecommendations = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const learnerId = location.state?.learnerId;
  const [loading, setLoading] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [recommending, setRecommending] = useState(false);
  const [businessGroupId, setBusinessGroupId] = useState(null);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    status: null,
    recommendedCourses: []
  });
  const [allCourses, setAllCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const token = localStorage.getItem("token");
  useEffect(() => {
    const fetchBusinessGroup = async () => {
      try {
        const response = await axios.get(API.businessGroup.getManagerDetail)
        setBusinessGroupId(response.data.id);
        (response.data)
      } catch (error) {
        console.error('Error fetching Business group :', error)
      }
    }
    fetchBusinessGroup()
  }, [])
  const RECOMMENDATIONS_API_URL = `${process.env.REACT_APP_API_URL}/TrainingRecommendation/Learner/Recommended-Courses`;
  const COURSES_API_URL = `${process.env.REACT_APP_API_URL}/Library/Get-Courses-List?${businessGroupId}`;
  const RECOMMEND_API_URL = `${process.env.REACT_APP_API_URL}/TrainingRequest/Manager/Create`;
  const handleRecommend = async (course) => {
    try {
      setRecommending(true);
      const token = localStorage.getItem("token");
      const response = await axios.post(RECOMMEND_API_URL, {
        learnerId: learnerId,
        courseId: course.id,
        isBudgetAllocated: false,
        budgetAmount: 0
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      notification.success({
        message: 'Recommendation Sent',
        description: `You have successfully recommended ${course.title}`,
        duration: 4.5
      });
    } catch (err) {
      console.error("Recommendation failed:", err);
      let errorMessage = "Failed to send recommendation";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      notification.error({
        message: 'Recommendation Failed',
        description: errorMessage,
        duration: 4.5
      });
    } finally {
      setRecommending(false);
      setSelectedCourse(null);
    }
  };
  const showRecommendConfirm = (course) => {
    setSelectedCourse(course);
    Modal.confirm({
      title: 'Confirm Recommendation',
      content: `Are you sure you want to recommend "${course.title}" to this Employee?`,
      okText: 'Yes, Recommend',
      cancelText: 'Cancel',
      onOk: () => handleRecommend(course),
      onCancel: () => setSelectedCourse(null)
    });
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token not found");
        }
        // Fetch recommended courses
        const recommendationsResponse = await axios.get(RECOMMENDATIONS_API_URL, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          params: { learnerId: learnerId || 3000 }
        });
        if (!recommendationsResponse.data) {
          throw new Error("No training recommendations available");
        }
        setData({
          status: recommendationsResponse.data.status,
          recommendedCourses: recommendationsResponse.data.recommendedCourses || []
        });
      } catch (err) {
        console.error("API Error:", err);
        let errorMessage = "Failed to load recommendations";
        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = "Endpoint not found. Please check the API URL";
          } else if (err.response.status === 401) {
            errorMessage = "Unauthorized - Please login again";
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        setError(errorMessage);
        notification.error({
          message: 'Error Loading Data',
          description: errorMessage,
          duration: 4.5
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [learnerId]);
  const fetchAllCourses = async () => {
    try {
      setLoadingCourses(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(COURSES_API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      setAllCourses(response.data || []);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
      notification.error({
        message: 'Error Loading Courses',
        description: 'Failed to load the course library',
        duration: 4.5
      });
    } finally {
      setLoadingCourses(false);
    }
  };
  const renderStatusMessage = () => {
    if (!data.status) return null;
    const statusInfo = statusMessages[data.status];
    return (
      <Card style={{ marginBottom: 24 }}>
        <Alert
          message={statusInfo.title}
          description={statusInfo.message}
          type={statusInfo.type}
          showIcon
        />
      </Card>
    );
  };
  const renderRecommendedCourses = () => (
    <Row gutter={[16, 16]}>
      {data.recommendedCourses.map((course) => (
        <Col span={12} key={course.id}>
          <Card
            size="small"
            style={{ height: '100%' }}
            title={
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Text strong ellipsis={{ tooltip: course.title }} >
                  {course.title}
                </Text>
                <Tag color="blue">{course.duration} days</Tag>
              </Space>
            }
            extra={
              <Button
                type="primary"
                onClick={() => showRecommendConfirm(course)}
                loading={recommending && selectedCourse?.id === course.id}
              >
                Recommend
              </Button>
            }
          >
            <Text ellipsis={{ tooltip: course.description }} style={{ marginBottom: 8 }}>
              {course.description}
            </Text>
            <Divider orientation="left" style={{ margin: '8px 0' }}>Skills</Divider>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {course.courseSkills?.map((skill) => (
                <div
                  key={skill.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 8px',
                    backgroundColor: skill.isMissingForLearner ? '#fff1f0' : '#f8f9f9ff',
                    borderRadius: 4
                  }}
                >
                  <Text ellipsis={{ tooltip: skill.name }} style={{ maxWidth: '70%' }}>
                    {skill.name}
                  </Text>
                  {skill.isMissingForLearner && <Tag color="error">Missing</Tag>}
                </div>
              ))}
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  );
  const renderAllCourses = () => {
    if (loadingCourses) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
          <Text>Loading course library...</Text>
        </div>
      );
    }
    return (
      <Collapse
        accordion
        expandIconPosition="end"
        expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
      >
        {allCourses.map(course => (
          <Panel
            header={
              <Space>
                <Text strong>{course.title}</Text>
                <Tag color="blue">{course.duration} days</Tag>
                {course.archived && <Tag color="red">Archived</Tag>}
              </Space>
            }
            key={course.id}
          >
            <Text>{course.description}</Text>
            <Divider orientation="left" style={{ margin: '16px 0 8px' }}>Skills</Divider>
            <List
              size="small"
              dataSource={course.courseSkills}
              renderItem={skill => (
                <List.Item>
                  <Text>{skill.name}</Text>
                </List.Item>
              )}
            />
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button
                type="primary"
                onClick={() => showRecommendConfirm(course)}
                loading={recommending && selectedCourse?.id === course.id}
              >
                Recommend
              </Button>
            </div>
          </Panel>
        ))}
      </Collapse>
    );
  };
  if (loading) {
    return (
      <Card style={{ margin: 24 }}>
        <div style={{ textAlign: "center", padding: 40 }}>
          <Progress type="circle" percent={30} width={80} />
          <Text>Loading your training recommendations...</Text>
        </div>
      </Card>
    );
  }
  if (error) {
    return (
      <Card style={{ margin: 24 }}>
        <Alert
          type="error"
          message="Error Loading Recommendations"
          description={
            <>
              <Text>{error}</Text>
              <div style={{ marginTop: 16 }}>
                <Button
                  type="primary"
                  onClick={() => window.location.reload()}
                  style={{ marginRight: 8 }}
                >
                  Retry
                </Button>
                <Button onClick={() => navigate(-1)}>
                  Go Back
                </Button>
              </div>
            </>
          }
        />
      </Card>
    );
  }
  return (
    <div style={{ margin: 24 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        Back
      </Button>
      {/* Status Message */}
      {renderStatusMessage()}
      <Card style={{ marginTop: 24 }}>
        <Tabs
          defaultActiveKey="1"
          onChange={(key) => {
            if (key === "2" && allCourses.length === 0) {
              fetchAllCourses();
            }
          }}
        >
          <TabPane tab="Recommended Courses" key="1">
            {data.status === RecommendationStatus.NeedsTraining ? (
              data.recommendedCourses.length > 0 ? (
                renderRecommendedCourses()
              ) : (
                <Alert message="No recommended courses found" type="info" />
              )
            ) : (
              <Alert
                message="No recommendations available"
                description="Complete your assessment or check back later for personalized recommendations."
                type="info"
              />
            )}
          </TabPane>
          <TabPane tab="Browse All Courses" key="2">
            {renderAllCourses()}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};
export default TrainingRecommendations;